import React, { useState, useEffect } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Autocomplete,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
  MenuList,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
} from '@mui/material';
import NewTableComponent from 'Component/NewTable';
import rootAxios from 'requests/rootAxios';
import { useNavigate } from 'react-router-dom';
import { restForm, deleteBatch } from 'reducer/trainingSlice';
import { useDispatch, useSelector } from 'react-redux';
import LoopIcon from '@mui/icons-material/Loop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';

import ChangeTrainerForm from 'Component/MultiStepTrainingForms/TrainingForm/ChangeTrainerForm';
import ChangeTrainingTypeForm from 'Component/MultiStepTrainingForms/TrainingForm/ChangeTrainingTypeForm';
import useCredentials from 'hooks/useCredentials';
import AddEmployeeForm from 'Component/MultiStepTrainingForms/TrainingForm/AddEmployeeForm';
import RemoveEmployeeForm from 'Component/MultiStepTrainingForms/TrainingForm/RemoveEmployeeForm';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewBatchModal from 'Component/ViewBatchModal';
import useIsLearningSuperUser from 'hooks/useIsLearningSuperUser';
import useIsLearningTrainer from 'hooks/useIsLearningTrainer';
import useIsClusterManager from 'hooks/useIsClusterManager';
import {
  fetchRegions,
  fetchAllTrainingTypes,
  fetchRegionsBranches,
} from 'thunks/trainingThunks/thunk';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import useIsLearningManager from 'hooks/useIsLearningManager';
import EmailModal from 'Component/MultiStepTrainingForms/TrainingForm/EmailModal';
import EmailModalCollection from 'Component/MultiStepTrainingForms/TrainingForm/EmailModalCollection';
dayjs.extend(customParseFormat);

const getStatusIcon = (row) => {
  const currentDate = dayjs();
  const parsedStartDate = dayjs(row.startDate, 'DD/MM/YYYY');
  const parsedEndDate = dayjs(row.endDate, 'DD/MM/YYYY');
  const isApproved = row.isApproved ?? false;
  const isStartingSoon = parsedStartDate.isAfter(currentDate);
  const isInProgress =
    !isStartingSoon &&
    (parsedStartDate.isSame(currentDate) || parsedStartDate.isBefore(currentDate)) &&
    (parsedEndDate.isSame(currentDate) || parsedEndDate.isAfter(currentDate));
  const isFinished = parsedEndDate.isBefore(currentDate);
  if (!isApproved) {
    return (
      <div>
        <WorkHistoryIcon color="error" />
        {`Approval Pending ${row.pendingAt}`}
      </div>
    );
  }
  if (isStartingSoon) {
    return (
      <div>
        <AccessAlarmIcon color="error" />
        Starting Soon
      </div>
    );
  }

  if (isInProgress) {
    const Icon = row.attendencePending || row.feedbackPending ? WarningAmberIcon : LoopIcon;
    const color = row.attendencePending || row.feedbackPending ? 'warning' : 'action';
    const text = row.attendencePending
      ? 'Attendance pending'
      : row.feedbackPending
        ? 'Feedback pending'
        : 'In Progress';

    return (
      <div>
        <Icon color={color} />
        {text}
      </div>
    );
  }

  if (isFinished) {
    const Icon = row.attendencePending || row.feedbackPending ? WarningAmberIcon : CheckCircleIcon;
    const color = row.attendencePending || row.feedbackPending ? 'warning' : 'success';
    const text = row.attendencePending
      ? 'Attendance pending'
      : row.feedbackPending
        ? 'Feedback pending'
        : 'Finished';

    return (
      <div>
        <Icon color={color} />
        {text}
      </div>
    );
  }

  return '';
};

const AllTrainings = () => {
  const dispatch = useDispatch();
  const [allCourses, setAllCourses] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const isLearningSuperUser = useIsLearningSuperUser();
  const isLearningTrainer = useIsLearningTrainer();
  const isLearningManager = useIsLearningManager();
  const navigate = useNavigate();
  const isClusterManager = useIsClusterManager();

  // Add new state for tabs and filtered data
  const [activeTab, setActiveTab] = useState(0);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [filteredCount, setFilteredCount] = useState(0);

  useEffect(() => {
    dispatch(restForm());
  }, [dispatch]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [reason, setReason] = useState('');
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  const [showChangeTrainerModal, setShowChangeTrainerModal] = useState(false);
  const [showChangeTrainingTypeModal, setShowChangeTrainingTypeModal] = useState(false);
  const [selectedBatchForTypeChange, setSelectedBatchForTypeChange] = useState(null);
  const [showViewBatchModal, setShowViewBatchModal] = useState(false);
  const [selectedBatchForView, setSelectedBatchForView] = useState(null);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [selectedBatchForAddEmployee, setSelectedBatchForAddEmployee] = useState(null);
  const [showRemoveEmployeeModal, setShowRemoveEmployeeModal] = useState(false);
  const [selectedBatchForRemoveEmployee, setSelectedBatchForRemoveEmployee] = useState(null);
  console.log('selectedBatchForRemoveEmployee', selectedBatchForRemoveEmployee);
  // Email Modal States
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showEmailModalCollection, setShowEmailModalCollection] = useState(false);
  const [selectedBatchForEmail, setSelectedBatchForEmail] = useState(null);
  const [batchEmailData, setBatchEmailData] = useState(null);
  const [emailModalLoading, setEmailModalLoading] = useState(false);
  const [shouldStopMail, setShouldStopMail] = useState(true);

  // Resend Email States
  const [showResendEmailDialog, setShowResendEmailDialog] = useState(false);
  const [resendEmailBatchId, setResendEmailBatchId] = useState('');
  const [resendEmailStep, setResendEmailStep] = useState('');
  const [resendEmailLoading, setResendEmailLoading] = useState(false);

  const handleResendEmailOpen = (batchId) => {
    setResendEmailBatchId(batchId);
    setResendEmailStep('');
    setShowResendEmailDialog(true);
    handleMenuClose();
  };

  const handleResendEmailClose = () => {
    setShowResendEmailDialog(false);
    setResendEmailBatchId('');
    setResendEmailStep('');
  };

  const handleResendEmailSubmit = async () => {
    const step = Number(resendEmailStep);
    if (!resendEmailBatchId || !step || step < 1 || step > 3) return;
    setResendEmailLoading(true);
    try {
      await rootAxios.post('/super-user/start-mailing-journey', {
        batchId: resendEmailBatchId,
        step,
      });
      toast.success(`Email for step ${step} sent successfully for batch ${resendEmailBatchId}`);
      handleResendEmailClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send email');
    } finally {
      setResendEmailLoading(false);
    }
  };

  // Change Batch Dates States
  const [showChangeDatesModal, setShowChangeDatesModal] = useState(false);
  const [selectedBatchForDates, setSelectedBatchForDates] = useState(null);
  const [batchDatesData, setBatchDatesData] = useState(null);
  const [changeDatesLoading, setChangeDatesLoading] = useState(false);

  // Pendency Modal States
  const [showPendencyModal, setShowPendencyModal] = useState(false);
  const [pendencyFilters, setPendencyFilters] = useState({
    trainingType: null,
    regions: [],
    branches: [],
  });
  const [pendencyData, setPendencyData] = useState([]);
  const [pendencyLoading, setPendencyLoading] = useState(false);
  const [regionIds, setRegionIds] = useState([]);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);

  // Get data from Redux
  const { availableRegions } = useSelector((state) => state.trainingSlice);
  const { availableBranches } = useSelector((state) => state.trainingSlicePrefetchData);
  const { trainingTypes } = useSelector((state) => state.trainingTypePageSlice.data);

  // Fetch regions and training types on component mount
  useEffect(() => {
    dispatch(fetchRegions());
    dispatch(fetchAllTrainingTypes());
  }, [dispatch]);

  // Fetch branches when regions change
  useEffect(() => {
    if (regionIds.length > 0) {
      dispatch(fetchRegionsBranches({ regions: regionIds }));
    }
  }, [regionIds, dispatch]);

  const renderAction = (row) => {
    const status = getStatusIcon(row)?.props.children[1];
    const isPending = status?.toLowerCase().includes('pending');
    const approvalKeywordList = ['attendance', 'feedback', 'finished'];
    const allApprovalIsDone = approvalKeywordList.some((keyword) =>
      status?.toLowerCase().includes(keyword)
    );

    console.log('isPending', status, allApprovalIsDone, isLearningTrainer);
    // Common menu items for all statuses
    const commonMenuItems = [
      {
        label: 'View Batch',
        icon: <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />,
        onClick: () => {
          setSelectedBatchForView(row.batchId);
          setShowViewBatchModal(true);
          handleMenuClose();
        },
      },
      {
        label: 'Edit',
        onClick: () => (window.location.href = `/training/?batch=${row.batchId}`),
      },
      {
        label: emailModalLoading && selectedRow === row ? 'Loading...' : 'Add/Edit Emails',
        disabled: emailModalLoading && selectedRow === row,
        onClick: () => {
          handleEmailModalOpen(row.batchId);
          handleMenuClose();
        },
      },
      {
        label: 'Add New Employee',
        onClick: () => {
          setSelectedBatchForAddEmployee(row.batchId);
          setShowAddEmployeeModal(true);
          handleMenuClose();
        },
      },
    ];

    if (isLearningManager || isLearningSuperUser || isClusterManager) {
      commonMenuItems.push({
        label: 'Change Batch Dates',
        onClick: () => {
          handleChangeDatesOpen(row.batchId);
          handleMenuClose();
        },
      });
    }

    // Menu items for pending status based on user role
    const getPendingMenuItems = () => {
      const items = [...commonMenuItems];

      // Add Mark Attendance for trainers, managers, and super users
      if (isLearningTrainer || isLearningManager || isLearningSuperUser || isClusterManager) {
        items.push({
          label: 'Mark Attendance',
          // Pass state to trigger re-renders
          onClick: () => (window.location.href = `/batch/attendence/?batchId=${row.batchId}`),
        });
      }

      // Add admin-only items for managers and super users
      if (isLearningManager || isLearningSuperUser || isClusterManager) {
        items.push(
          {
            label: 'Add New Employee',
            onClick: () => {
              setSelectedBatchForAddEmployee(row.batchId);
              setShowAddEmployeeModal(true);
              handleMenuClose();
            },
          },
          {
            label: 'Remove Employee',
            onClick: () => {
              setSelectedBatchForRemoveEmployee(row);
              setShowRemoveEmployeeModal(true);
              handleMenuClose();
            },
          },
          {
            label: 'Change Trainer',
            onClick: () => {
              setMenuAnchor(null);
              setSelectedRow(row);
              setShowChangeTrainerModal(true);
            },
          },
          {
            label: 'Delete Batch',
            onClick: () => handleDeleteClick(row.batchId),
          },
          {
            label: 'Change Training Type',
            onClick: () => {
              setMenuAnchor(null);
              setSelectedBatchForTypeChange(row);
              setShowChangeTrainingTypeModal(true);
            },
          },
          {
            label: 'Resend Email',
            onClick: () => handleResendEmailOpen(row.batchId),
          }
        );
      }

      // add trainer only if any approval keyword is present
      if (!allApprovalIsDone && isLearningTrainer) {
        console.log('pusing add new employee');
        items.push({
          label: 'Add New Employee',
          onClick: () => {
            setSelectedBatchForAddEmployee(row.batchId);
            setShowAddEmployeeModal(true);
            handleMenuClose();
          },
        });
        items.push({
          label: 'Remove Employee',
          onClick: () => {
            setSelectedBatchForRemoveEmployee(row);
            setShowRemoveEmployeeModal(true);
            handleMenuClose();
          },
        });
      }
      return items;
    };

    // Menu items for non-pending status
    const getNonPendingMenuItems = () => {
      const items = [...commonMenuItems];

      // Add admin-only items for managers and super users
      if (isLearningManager || isLearningSuperUser || isClusterManager) {
        items.push(
          {
            label: 'Add New Employee',
            onClick: () => {
              setSelectedBatchForAddEmployee(row.batchId);
              setShowAddEmployeeModal(true);
              handleMenuClose();
            },
          },
          {
            label: 'Remove Employee',
            onClick: () => {
              setSelectedBatchForRemoveEmployee(row);
              setShowRemoveEmployeeModal(true);
              handleMenuClose();
            },
          },
          {
            label: 'Change Trainer',
            onClick: () => {
              setMenuAnchor(null);
              setSelectedRow(row);
              setShowChangeTrainerModal(true);
            },
          },
          {
            label: 'Delete Batch',
            onClick: () => handleDeleteClick(row.batchId),
          },
          {
            label: 'View Attendance',
            onClick: () => (window.location.href = `/batch/attendence/?batchId=${row.batchId}`),
          },
          {
            label: 'Change Training Type',
            onClick: () => {
              setMenuAnchor(null);
              setSelectedBatchForTypeChange(row);
              setShowChangeTrainingTypeModal(true);
            },
          },
          {
            label: 'Resend Email',
            onClick: () => handleResendEmailOpen(row.batchId),
          }
        );
      }
      // if status
      if (!allApprovalIsDone && isLearningTrainer) {
        console.log('pusing add new employee');
        items.push({
          label: 'Add New Employee',
          onClick: () => {
            setSelectedBatchForAddEmployee(row.batchId);
            setShowAddEmployeeModal(true);
            handleMenuClose();
          },
        });
        items.push({
          label: 'Remove Employee',
          onClick: () => {
            setSelectedBatchForRemoveEmployee(row);
            setShowRemoveEmployeeModal(true);
            handleMenuClose();
          },
        });
      }
      return items;
    };

    const tempMenuItems = isPending ? getPendingMenuItems() : getNonPendingMenuItems();
    const menuItems = tempMenuItems.reduce((unique, item) => {
      if (!unique.some((i) => i.label === item.label)) {
        unique.push(item);
      }
      return unique;
    }, []);

    return (
      <>
        <Button variant="contained" color="primary" onClick={(e) => handleMenuOpen(e, row)}>
          Edit
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor) && selectedRow === row}
          onClose={handleMenuClose}
        >
          {menuItems.map((item, index) => (
            <MenuItem key={index} onClick={item.onClick}>
              {item.icon}
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };
  const columns = [
    {
      label: 'Batch ID',
      field: 'batchId',
      sortable: true,
      width: '120px',
    },
    {
      label: 'Start Date',
      field: 'startDate',
      sortable: true,
      width: '120px',
      sortValue: (row) => {
        // Convert DD/MM/YYYY string to dayjs object for proper sorting
        return dayjs(row.startDate, 'DD/MM/YYYY').toDate();
      },
    },
    {
      label: 'End Date',
      field: 'endDate',
      sortable: true,
      width: '120px',
      sortValue: (row) => {
        // Convert DD/MM/YYYY string to dayjs object for proper sorting
        return dayjs(row.endDate, 'DD/MM/YYYY').toDate();
      },
    },
    {
      label: 'No. of Trainees',
      field: 'listOfTrainess',
      sortable: true,
      width: '150px',
    },
    {
      label: 'Status',
      field: 'status',
      sortable: true,
      width: '200px',
      render: (row) => getStatusIcon(row),
      sortValue: (row) => {
        const status = getStatusIcon(row)?.props.children[1];
        return status || '';
      },
    },
    {
      label: 'Action',
      field: 'edit',
      sortable: false,
      width: '150px',
      render: (row) => renderAction(row),
    },
  ];

  // Function to categorize batches by status
  const categorizeBatches = (batches) => {
    const currentDate = dayjs();

    return batches.reduce(
      (acc, batch) => {
        const parsedStartDate = dayjs(batch.startDate, 'DD/MM/YYYY');
        const parsedEndDate = dayjs(batch.endDate, 'DD/MM/YYYY');
        const isApproved = batch.isApproved ?? false;
        const isStartingSoon = parsedStartDate.isAfter(currentDate);
        const isInProgress =
          !isStartingSoon &&
          (parsedStartDate.isSame(currentDate) || parsedStartDate.isBefore(currentDate)) &&
          (parsedEndDate.isSame(currentDate) || parsedEndDate.isAfter(currentDate));
        const isFinished = parsedEndDate.isBefore(currentDate);

        // Check for attendance or feedback pending
        const hasActionPending = batch.attendencePending || batch.feedbackPending;

        if (!isApproved) {
          acc.approvalPending.push(batch);
        } else if (isStartingSoon) {
          acc.startingSoon.push(batch);
        } else if (isInProgress) {
          if (hasActionPending) {
            acc.actionPending.push(batch);
          } else {
            acc.ongoing.push(batch);
          }
        } else if (isFinished) {
          if (hasActionPending) {
            acc.actionPending.push(batch);
          } else {
            acc.completed.push(batch);
          }
        }

        return acc;
      },
      {
        completed: [],
        ongoing: [],
        startingSoon: [],
        approvalPending: [],
        actionPending: [],
      }
    );
  };

  // Function to get ongoing batches including action pending and approval pending
  const getOngoingBatches = (batches) => {
    const categorized = categorizeBatches(batches);
    return [...categorized.ongoing, ...categorized.actionPending, ...categorized.approvalPending];
  };

  // Function to get data for active tab
  const getDataForActiveTab = () => {
    const categorized = categorizeBatches(allCourses);
    const tabData = {
      0: { data: allCourses, label: 'All' },
      1: { data: categorized.completed, label: 'Completed' },
      2: { data: getOngoingBatches(allCourses), label: 'Ongoing' },
      3: { data: categorized.startingSoon, label: 'Starting Soon' },
      4: { data: categorized.approvalPending, label: 'Approval Pending' },
      5: { data: categorized.actionPending, label: 'Action Pending' },
    };

    return tabData[activeTab] || { data: [], label: 'All' };
  };

  // Update filtered data when active tab or allCourses changes
  useEffect(() => {
    const { data } = getDataForActiveTab();
    setFilteredCourses(data);
    setFilteredCount(data.length);
  }, [activeTab, allCourses]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleShowCourses = async () => {
    try {
      setLoading(true);
      const response = await rootAxios.get(`/user/getAllBatches/?page=alltraining`);
      if (response) {
        const data = response?.data?.data?.listOfBatches || [];
        setAllCourses(data);
        setCount(data.length);
      }
    } catch (error) {
      console.error('AllTrainings - API Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleShowCourses();
  }, []);

  const handleDeleteClick = (batchId) => {
    const batchToDelete = allCourses.find((batch) => batch.batchId === batchId);
    setSelectedBatch(batchToDelete);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!reason) {
        toast.error('Please enter a reason');
        return;
      }
      if (selectedBatch?.batchId) {
        const result = await dispatch(
          deleteBatch({ batchId: selectedBatch.batchId, reason })
        ).unwrap();
        console.log('Delete result:', result);
        toast.success('Batch deleted successfully');
        setDeleteDialogOpen(false);
        handleShowCourses(); // Refresh the table data
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error?.message || 'Failed to delete batch');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedBatch(null);
  };

  // Email Modal Functions
  const handleEmailModalOpen = async (batchId) => {
    try {
      setEmailModalLoading(true);
      setSelectedBatchForEmail(batchId);

      // Close any existing modals first
      setShowEmailModal(false);
      setShowEmailModalCollection(false);
      setBatchEmailData(null);

      // Fetch batch data to get training type and email configuration
      const response = await rootAxios.post('/user/getBatch', { batchId });
      const batchData = response?.data?.data?.batchData?.[0];

      if (batchData) {
        // Ensure email configuration has the correct structure
        let emailConfig = {
          totalSteps: batchData.trainingType?.toLowerCase() === 'collection_training' ? 2 : 3,
          steps: [],
        };

        // If existing email config exists, use it and ensure proper structure
        if (batchData.emailConfig?.steps && Array.isArray(batchData.emailConfig.steps)) {
          emailConfig = {
            totalSteps: batchData.emailConfig.totalSteps || emailConfig.totalSteps,
            steps: batchData.emailConfig.steps.map((step) => ({
              to: Array.isArray(step.to) ? step.to : [],
              cc: Array.isArray(step.cc) ? step.cc : [],
            })),
          };
        } else {
          // Create default empty structure
          const numSteps = batchData.trainingType?.toLowerCase() === 'collection_training' ? 2 : 3;
          emailConfig.steps = Array(numSteps)
            .fill(null)
            .map(() => ({ to: [], cc: [] }));
        }

        const formattedBatchData = {
          ...batchData,
          emailConfig,
        };

        setBatchEmailData(formattedBatchData);

        // Determine which modal to show based on training type
        // Use setTimeout to ensure state is set before opening modal
        setTimeout(() => {
          if (batchData.trainingType?.toLowerCase() === 'collection_training') {
            setShowEmailModalCollection(true);
          } else {
            setShowEmailModal(true);
          }
        }, 0);
      } else {
        toast.error('Batch data not found');
      }
    } catch (error) {
      console.error('Error fetching batch data for email modal:', error);
      toast.error('Failed to fetch batch data');
    } finally {
      setEmailModalLoading(false);
    }
  };

  const handleEmailModalClose = () => {
    setShowEmailModal(false);
    setShowEmailModalCollection(false);
    setSelectedBatchForEmail(null);
    setBatchEmailData(null);
  };

  // Change Batch Dates Handlers
  const handleChangeDatesOpen = async (batchId) => {
    try {
      setChangeDatesLoading(true);
      const response = await rootAxios.post('/user/getBatch', { batchId });
      const batchData = response?.data?.data?.batchData?.[0];
      if (batchData) {
        setSelectedBatchForDates(batchId);
        setBatchDatesData(batchData);
        setShowChangeDatesModal(true);
      } else {
        toast.error('Batch data not found');
      }
    } catch (error) {
      toast.error('Failed to fetch batch data');
    } finally {
      setChangeDatesLoading(false);
    }
  };

  const handleChangeDatesClose = () => {
    setShowChangeDatesModal(false);
    setSelectedBatchForDates(null);
    setBatchDatesData(null);
  };

  const handleChangeDatesSubmit = async (newDates, newDatesEnd) => {
    try {
      await rootAxios.post('/user/change-batch-dates', {
        batchId: selectedBatchForDates,
        dates: newDates,
        datesEnd: newDatesEnd,
      });
      toast.success('Batch dates updated successfully');
      handleChangeDatesClose();
      handleShowCourses();
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to update dates';
      toast.error(msg);
    }
  };

  const handleEmailSubmit = async (emailData) => {
    try {
      if (!batchEmailData?.batchId) {
        toast.error('No batch data available');
        return;
      }

      // Transform emailData into the required format based on training type
      let formattedEmailConfig;

      if (batchEmailData.trainingType?.toLowerCase() === 'collection_training') {
        // For collection training, 2 steps
        formattedEmailConfig = {
          totalSteps: 2,
          steps: [
            {
              to: Array.isArray(emailData.stage1?.to) ? emailData.stage1.to : [],
              cc: Array.isArray(emailData.stage1?.cc) ? emailData.stage1.cc : [],
            },
            {
              to: Array.isArray(emailData.stage2?.to) ? emailData.stage2.to : [],
              cc: Array.isArray(emailData.stage2?.cc) ? emailData.stage2.cc : [],
            },
          ],
        };
      } else {
        // For induction and other types, 3 steps
        formattedEmailConfig = {
          totalSteps: 3,
          steps: [
            {
              to: Array.isArray(emailData.stage1?.to) ? emailData.stage1.to : [],
              cc: Array.isArray(emailData.stage1?.cc) ? emailData.stage1.cc : [],
            },
            {
              to: Array.isArray(emailData.stage2?.to) ? emailData.stage2.to : [],
              cc: Array.isArray(emailData.stage2?.cc) ? emailData.stage2.cc : [],
            },
            {
              to: Array.isArray(emailData.stage3?.to) ? emailData.stage3.to : [],
              cc: Array.isArray(emailData.stage3?.cc) ? emailData.stage3.cc : [],
            },
          ],
        };
      }

      // Update the batch with new email configuration
      const updatePayload = {
        ...batchEmailData,
        emailConfig: formattedEmailConfig,
      };

      const formData = new FormData();
      formData.append('trainingFormDetail', JSON.stringify(updatePayload));
      formData.append('updateReason', 'email-configuration');
      formData.append('shouldStopMail', shouldStopMail);

      await rootAxios.put('/user/updateBatch', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Email configuration updated successfully');
      handleEmailModalClose();
      handleShowCourses(); // Refresh the table data
    } catch (error) {
      console.error('Error updating email configuration:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to update email configuration';
      toast.error(errorMessage);
    }
  };

  // Pendency Modal Functions
  const handlePendencyModalOpen = () => {
    setShowPendencyModal(true);
    setPendencyData([]);
    setPendencyFilters({
      trainingType: null,
      regions: [],
      branches: [],
    });
  };

  const handlePendencyModalClose = () => {
    setShowPendencyModal(false);
    setPendencyData([]);
    setPendencyFilters({
      trainingType: null,
      regions: [],
      branches: [],
    });
  };

  const handleRegionChange = (event, value) => {
    setPendencyFilters((prev) => ({ ...prev, regions: value }));
    if (value.length === 0) {
      setPendencyFilters((prev) => ({ ...prev, branches: [] }));
      setRegionIds([]);
    } else {
      const regionIds = value.map((item) => item.id);
      setRegionIds(regionIds);
    }
  };

  const handleBranchChange = (event, value) => {
    setPendencyFilters((prev) => ({ ...prev, branches: value }));
  };

  const handleSelectAllRegions = () => {
    const allRegions = availableRegions || [];
    setPendencyFilters((prev) => ({ ...prev, regions: allRegions }));
    const regionIds = allRegions.map((item) => item.id);
    setRegionIds(regionIds);
  };

  const handleSelectAllBranches = () => {
    const allBranches = availableBranches || [];
    setPendencyFilters((prev) => ({ ...prev, branches: allBranches }));
  };

  const handleClearAllRegions = () => {
    setPendencyFilters((prev) => ({ ...prev, regions: [], branches: [] }));
    setRegionIds([]);
  };

  const handleClearAllBranches = () => {
    setPendencyFilters((prev) => ({ ...prev, branches: [] }));
  };

  const handleDownloadMenuOpen = (event) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleDownload = (format) => {
    downloadPendencyData(format);
    handleDownloadMenuClose();
  };

  const fetchPendencyData = async () => {
    if (
      !pendencyFilters.trainingType ||
      pendencyFilters.regions.length === 0 ||
      pendencyFilters.branches.length === 0
    ) {
      toast.error('Please select training type, regions, and branches');
      return;
    }

    setPendencyLoading(true);
    try {
      const payload = {
        trainingType: pendencyFilters.trainingType.trainingName || pendencyFilters.trainingType,
        region: pendencyFilters.regions,
        branches: pendencyFilters.branches,
      };

      const response = await rootAxios.post('/user/getLastestEmployees', payload, {
        params: {
          skip: 0,
          limit: 1000,
        },
      });

      if (response?.data?.data?.employees) {
        setPendencyData(response.data.data.employees);
        toast.success(
          `Found ${response.data.data.employees.length} employees with pending training`
        );
      } else {
        setPendencyData([]);
        toast.info('No employees found with pending training for the selected criteria');
      }
    } catch (error) {
      console.error('Error fetching pendency data:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to fetch pendency data';
      toast.error(errorMessage);
      setPendencyData([]);
    } finally {
      setPendencyLoading(false);
    }
  };

  const downloadPendencyData = (format = 'excel') => {
    if (pendencyData.length === 0) {
      toast.error('No data to download');
      return;
    }

    try {
      // Transform data for download
      const downloadData = pendencyData.map((employee) => ({
        'Employee ID': employee.employeeId || '',
        'Employee Name': employee.employeeName || '',
        'Functional Title': employee.funcTitleName || '',
        'Date of Joining': employee.dateOfJoining || '',
        Region: employee.regionName || '',
        Branch: employee.branchName || '',
        'Training Type':
          pendencyFilters.trainingType?.trainingName || pendencyFilters.trainingType || '',
        'Nomination Count': employee.nominationCount || 0,
      }));

      const trainingTypeName =
        pendencyFilters.trainingType?.trainingName || pendencyFilters.trainingType || 'unknown';
      const sanitizedTrainingType = trainingTypeName.replace(/[^a-zA-Z0-9]/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];

      if (format === 'excel') {
        // Create workbook and worksheet
        const ws = XLSX.utils.json_to_sheet(downloadData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Pending Training Employees');

        // Generate filename
        const fileName = `pending_training_${sanitizedTrainingType}_${dateStr}.xlsx`;

        // Download file
        XLSX.writeFile(wb, fileName);
        toast.success('Excel file downloaded successfully');
      } else if (format === 'csv') {
        // Convert to CSV
        const headers = Object.keys(downloadData[0]);
        const csvContent = [
          headers.join(','),
          ...downloadData.map((row) =>
            headers
              .map((header) => {
                const value = row[header];
                // Escape quotes and wrap in quotes if contains comma or quotes
                const escapedValue = String(value).replace(/"/g, '""');
                return escapedValue.includes(',') ||
                  escapedValue.includes('"') ||
                  escapedValue.includes('\n')
                  ? `"${escapedValue}"`
                  : escapedValue;
              })
              .join(',')
          ),
        ].join('\n');

        // Create and download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `pending_training_${sanitizedTrainingType}_${dateStr}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success('CSV file downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading data:', error);
      toast.error('Failed to download data');
    }
  };

  // Pendency table columns
  const pendencyColumns = [
    {
      label: 'Employee ID',
      field: 'employeeId',
      sortable: true,
      width: '120px',
    },
    {
      label: 'Employee Name',
      field: 'employeeName',
      sortable: true,
      width: '200px',
    },
    {
      label: 'Functional Title',
      field: 'funcTitleName',
      sortable: true,
      width: '200px',
    },
    {
      label: 'Date of Joining',
      field: 'dateOfJoining',
      sortable: true,
      width: '150px',
    },
    {
      label: 'Region',
      field: 'regionName',
      sortable: true,
      width: '150px',
    },
    {
      label: 'Branch',
      field: 'branchName',
      sortable: true,
      width: '150px',
    },
    {
      label: 'Nomination Count',
      field: 'nominationCount',
      sortable: true,
      width: '150px',
      render: (row) => row.nominationCount || 0,
    },
  ];

  return (
    <>
      <Layout>
        <Box
          sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            p: { xs: 2, md: 3 },
          }}
        >
          {/* Professional Header */}
          <Box
            sx={{
              mb: 2,
              background: '#ffffff',
              borderRadius: '8px',
              p: 2,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e2e8f0',
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <Box>
                <Typography
                  variant="h5"
                  fontWeight="600"
                  sx={{
                    color: '#1e293b',
                    mb: 0.25,
                  }}
                >
                  All Trainings
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                  Manage and monitor all training batches across your organization
                </Typography>
              </Box>
              <Box display="flex" gap={2} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={handlePendencyModalOpen}
                  sx={{
                    borderRadius: '12px',
                    px: 3,
                    py: 1.5,
                    fontWeight: '600',
                    textTransform: 'none',
                    borderColor: '#3b82f6',
                    color: '#3b82f6',
                    '&:hover': {
                      borderColor: '#1d4ed8',
                      backgroundColor: 'rgba(59, 130, 246, 0.04)',
                    },
                  }}
                >
                  Show Pendency
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    navigate('/training/create-batch');
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    borderRadius: '12px',
                    px: 3,
                    py: 1.5,
                    fontWeight: '600',
                    textTransform: 'none',
                    boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px 0 rgba(59, 130, 246, 0.4)',
                    },
                  }}
                >
                  Create Batch
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Status Summary Cards */}
          {/* <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2, flexWrap: 'wrap' }}>
          {(() => {
            const categorized = categorizeBatches(allCourses);
            return [
              { label: 'All', count: allCourses.length, color: 'primary.main', icon: null, tabIndex: 0 },
              { label: 'Completed', count: categorized.completed.length, color: 'success.main', icon: <CheckCircleIcon />, tabIndex: 1 },
              { label: 'Ongoing', count: categorized.ongoing.length, color: 'info.main', icon: <LoopIcon />, tabIndex: 2 },
              { label: 'Starting Soon', count: categorized.startingSoon.length, color: 'warning.main', icon: <AccessAlarmIcon />, tabIndex: 3 },
              { label: 'Approval Pending', count: categorized.approvalPending.length, color: 'error.main', icon: <WorkHistoryIcon />, tabIndex: 4 },
              { label: 'Action Pending', count: categorized.actionPending.length, color: 'warning.main', icon: <WarningAmberIcon />, tabIndex: 5 }
            ].map((item, index) => (
                              <Paper
                  key={index}
                  elevation={activeTab === item.tabIndex ? 3 : 1}
                  sx={{
                    p: 2,
                    minWidth: '140px',
                    textAlign: 'center',
                    border: `2px solid ${item.color}`,
                    borderRadius: 2,
                    backgroundColor: activeTab === item.tabIndex ? `${item.color}20` : `${item.color}10`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    transform: activeTab === item.tabIndex ? 'translateY(-2px)' : 'none',
                    boxShadow: activeTab === item.tabIndex ? `0 4px 12px ${item.color}40` : 'none',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 12px ${item.color}40`,
                    }
                  }}
                  onClick={() => setActiveTab(item.tabIndex)}
                >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  {item.icon && (
                    <Box sx={{ color: item.color, mr: 1 }}>
                      {item.icon}
                    </Box>
                  )}
                  <Typography variant="h6" sx={{ color: item.color, fontWeight: 600 }}>
                    {item.count}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                  {item.label}
                </Typography>
              </Paper>
            ));
          })()}
        </Box> */}

          {/* Status Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="training status tabs"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  minHeight: '48px',
                  px: 3,
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">All</Typography>
                    <Chip
                      label={allCourses.length}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                    <Typography variant="body2">Completed</Typography>
                    <Chip
                      label={categorizeBatches(allCourses).completed.length}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LoopIcon sx={{ fontSize: 20, color: 'info.main' }} />
                    <Typography variant="body2">Ongoing</Typography>
                    <Chip
                      label={getOngoingBatches(allCourses).length}
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessAlarmIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                    <Typography variant="body2">Upcoming</Typography>
                    <Chip
                      label={categorizeBatches(allCourses).startingSoon.length}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WorkHistoryIcon sx={{ fontSize: 20, color: 'error.main' }} />
                    <Typography variant="body2">Approval Pending</Typography>
                    <Chip
                      label={categorizeBatches(allCourses).approvalPending.length}
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                    />
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                    <Typography variant="body2">Action Pending</Typography>
                    <Chip
                      label={categorizeBatches(allCourses).actionPending.length}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ minWidth: '20px', height: '20px', fontSize: '0.7rem' }}
                    />
                  </Box>
                }
              />
            </Tabs>
          </Box>

          {/* Table Section */}
          <Card
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid #e2e8f0',
              borderRadius: '20px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              mb: 4,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <NewTableComponent
                tableData={filteredCourses}
                columns={columns}
                tableName={`${getDataForActiveTab().label} Trainings`}
                count={filteredCount}
                loading={loading}
                enableSearch={true}
                enableFilters={false}
                searchPlaceholder={`Search ${getDataForActiveTab().label.toLowerCase()} batches...`}
                useWholeRowClicked={false}
                emptyMessage={`No ${getDataForActiveTab().label.toLowerCase()} batches found`}
                maxHeight="600px"
              />
            </CardContent>
          </Card>
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Batch</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the batch "{selectedBatch?.batchId}"? This action
              cannot be undone.
            </Typography>
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showChangeTrainerModal}
          onClose={() => setShowChangeTrainerModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Change Trainer</DialogTitle>
          <DialogContent>
            <ChangeTrainerForm
              employee={[]}
              batchIdReceived={selectedRow?.batchId}
              disabled={['employee']}
              trigger={() => {
                //dispatch(fetchAllTrainers());
              }}
              closeModal={() => setShowChangeTrainerModal(false)}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={showChangeTrainingTypeModal}
          onClose={() => {
            setShowChangeTrainingTypeModal(false);
            setSelectedBatchForTypeChange(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Change Training Type</DialogTitle>
          <DialogContent>
            <ChangeTrainingTypeForm
              batchId={selectedBatchForTypeChange?.batchId}
              currentTrainingType={selectedBatchForTypeChange?.trainingType}
              closeModal={() => {
                setShowChangeTrainingTypeModal(false);
                setSelectedBatchForTypeChange(null);
              }}
              trainingTypes={trainingTypes || []}
            />
          </DialogContent>
        </Dialog>

        {/* Add ViewBatchModal */}
        <ViewBatchModal
          open={showViewBatchModal}
          onClose={() => {
            setShowViewBatchModal(false);
            setSelectedBatchForView(null);
          }}
          batchId={selectedBatchForView}
        />

        {/* Add Employee Modal */}
        <Dialog
          open={showAddEmployeeModal}
          onClose={() => setShowAddEmployeeModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Employees to Batch</DialogTitle>
          <DialogContent>
            <AddEmployeeForm
              batchIdReceived={selectedBatchForAddEmployee}
              onSubmit={() => {
                setShowAddEmployeeModal(false);
                setSelectedBatchForAddEmployee(null);
                handleShowCourses(); // Refresh the table data
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Remove Employee Modal */}
        <Dialog
          open={showRemoveEmployeeModal}
          onClose={() => setShowRemoveEmployeeModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Remove Employees from Batch</DialogTitle>
          <DialogContent>
            <RemoveEmployeeForm
              batchIdReceived={selectedBatchForRemoveEmployee?.batchId}
              trainingType={selectedBatchForRemoveEmployee?.trainingType}
              onSubmit={() => {
                setShowRemoveEmployeeModal(false);
                setSelectedBatchForRemoveEmployee(null);
                handleShowCourses(); // Refresh the table data
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Pendency Modal */}
        <Dialog
          open={showPendencyModal}
          onClose={handlePendencyModalClose}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            },
          }}
        >
          <DialogTitle
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              pb: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Training Pendency Report
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  View employees with pending training based on selected criteria
                </Typography>
              </Box>
              {pendencyData.length > 0 && (
                <Box>
                  <Tooltip title="Download Report">
                    <IconButton
                      onClick={handleDownloadMenuOpen}
                      sx={{
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={downloadMenuAnchor}
                    open={Boolean(downloadMenuAnchor)}
                    onClose={handleDownloadMenuClose}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 150,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: 1.5,
                      },
                    }}
                  >
                    <MenuList>
                      <MenuItem onClick={() => handleDownload('excel')} sx={{ py: 1.5 }}>
                        <DownloadIcon sx={{ mr: 1.5, fontSize: 20 }} />
                        Download Excel
                      </MenuItem>
                      <MenuItem onClick={() => handleDownload('csv')} sx={{ py: 1.5 }}>
                        <DownloadIcon sx={{ mr: 1.5, fontSize: 20 }} />
                        Download CSV
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Box>
              )}
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {/* Filters Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.primary', fontWeight: 600 }}>
                Select Filters
              </Typography>

              <Grid container spacing={3}>
                {/* Training Type */}
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                    Training Type *
                  </Typography>
                  <Autocomplete
                    options={trainingTypes || []}
                    getOptionLabel={(option) => option.trainingName || ''}
                    value={pendencyFilters.trainingType}
                    onChange={(event, value) =>
                      setPendencyFilters((prev) => ({ ...prev, trainingType: value }))
                    }
                    PaperComponent={(paperProps) => {
                      const { children, ...restPaperProps } = paperProps;
                      return (
                        <Paper
                          {...restPaperProps}
                          style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                          }}
                          sx={{
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: '#f1f1f1',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: '#c1c1c1',
                              borderRadius: '4px',
                              '&:hover': {
                                background: '#a8a8a8',
                              },
                            },
                          }}
                        >
                          {children}
                        </Paper>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select training type"
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Regions */}
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      Regions *
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={handleSelectAllRegions}
                        sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={handleClearAllRegions}
                        sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Box>
                  <Autocomplete
                    multiple
                    options={availableRegions || []}
                    getOptionLabel={(option) => option.label || ''}
                    value={pendencyFilters.regions}
                    onChange={handleRegionChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        paddingRight: '65px !important',
                        maxHeight: '100px !important',
                        overflow: 'scroll !important',
                      },
                    }}
                    PaperComponent={(paperProps) => {
                      const { children, ...restPaperProps } = paperProps;
                      return (
                        <Paper
                          {...restPaperProps}
                          sx={{
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: '#f1f1f1',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: '#c1c1c1',
                              borderRadius: '4px',
                              '&:hover': {
                                background: '#a8a8a8',
                              },
                            },
                          }}
                        >
                          {children}
                        </Paper>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select regions"
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.label}
                          size="small"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ))
                    }
                  />
                </Grid>

                {/* Branches */}
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      Branches *
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={handleSelectAllBranches}
                        disabled={pendencyFilters.regions.length === 0}
                        sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                      >
                        Select All
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={handleClearAllBranches}
                        disabled={pendencyFilters.regions.length === 0}
                        sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Box>
                  <Autocomplete
                    multiple
                    options={availableBranches || []}
                    getOptionLabel={(option) => option.label || ''}
                    value={pendencyFilters.branches}
                    onChange={handleBranchChange}
                    disabled={pendencyFilters.regions.length === 0}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        paddingRight: '65px !important',
                        maxHeight: '100px !important',
                        overflow: 'scroll !important',
                      },
                    }}
                    PaperComponent={(paperProps) => {
                      const { children, ...restPaperProps } = paperProps;
                      return (
                        <Paper
                          {...restPaperProps}
                          sx={{
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: '#f1f1f1',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: '#c1c1c1',
                              borderRadius: '4px',
                              '&:hover': {
                                background: '#a8a8a8',
                              },
                            },
                          }}
                        >
                          {children}
                        </Paper>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={
                          pendencyFilters.regions.length === 0
                            ? 'Select regions first'
                            : 'Select branches'
                        }
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1.5,
                          },
                        }}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.id}
                          label={option.label}
                          size="small"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ))
                    }
                  />
                </Grid>
              </Grid>

              {/* Action Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={fetchPendencyData}
                  disabled={
                    pendencyLoading ||
                    !pendencyFilters.trainingType ||
                    pendencyFilters.regions.length === 0 ||
                    pendencyFilters.branches.length === 0
                  }
                  startIcon={pendencyLoading ? <CircularProgress size={20} /> : null}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
                    },
                  }}
                >
                  {pendencyLoading ? 'Fetching Data...' : 'Generate Report'}
                </Button>
              </Box>
            </Box>

            {/* Results Section */}
            {pendencyData.length > 0 && (
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 2,
                  border: '1px solid #e9ecef',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Results ({pendencyData.length} employees found)
                  </Typography>
                  <Chip
                    label={`Training: ${pendencyFilters.trainingType?.trainingName || pendencyFilters.trainingType}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Box>

                <NewTableComponent
                  tableData={pendencyData}
                  columns={pendencyColumns}
                  tableName="Pending Training Employees"
                  count={pendencyData.length}
                  loading={false}
                  enableSearch={true}
                  enableFilters={false}
                  searchPlaceholder="Search employees..."
                  useWholeRowClicked={false}
                  emptyMessage="No pending employees found"
                  maxHeight="400px"
                  elevation={0}
                  borderRadius={1}
                  getRowStyle={(row) => {
                    // Apply red/white highlight logic similar to drag n drop boxes
                    const nominationCount = row.nominationCount || 0;

                    // Handle date parsing with error checking
                    let daysDiff = 0;
                    try {
                      if (row.dateOfJoining && row.dateOfJoining.includes('-')) {
                        const [day, month, year] = row.dateOfJoining.split('-').map(Number);
                        if (day && month && year) {
                          const dateOfJoining = new Date(year, month - 1, day);
                          const currentDate = new Date();
                          daysDiff = Math.floor(
                            (currentDate - dateOfJoining) / (1000 * 60 * 60 * 24)
                          );
                        }
                      }
                    } catch (error) {
                      console.warn('Error parsing date:', row.dateOfJoining, error);
                    }

                    if (nominationCount > 1 || daysDiff >= 15) {
                      return { backgroundColor: '#ef9a9a' }; // Red background
                    }
                    return { backgroundColor: '#f5f5f5' }; // White background
                  }}
                />
              </Box>
            )}

            {/* No Results Message */}
            {pendencyData.length === 0 && !pendencyLoading && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'text.secondary',
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No Data Available
                </Typography>
                <Typography variant="body2">
                  Select filters and click "Generate Report" to view pending training employees
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              onClick={handlePendencyModalClose}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Change Batch Dates Dialog */}
        {showChangeDatesModal && batchDatesData && (
          <ChangeBatchDatesDialog
            open={showChangeDatesModal}
            onClose={handleChangeDatesClose}
            onSubmit={handleChangeDatesSubmit}
            batchData={batchDatesData}
          />
        )}

        {/* Resend Email Dialog */}
        <Dialog
          open={showResendEmailDialog}
          onClose={handleResendEmailClose}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Resend Email</DialogTitle>
          <DialogContent>
            <TextField
              label="Batch ID"
              value={resendEmailBatchId}
              onChange={(e) => setResendEmailBatchId(e.target.value)}
              fullWidth
              size="small"
              sx={{ mt: 1, mb: 2 }}
            />
            <Autocomplete
              options={[
                { label: 'Step 1 — RM Approval', value: 1 },
                { label: 'Step 2 — Circle Head Approval', value: 2 },
                { label: 'Step 3 — Invitation Mail', value: 3 },
              ]}
              getOptionLabel={(o) => o.label}
              value={
                resendEmailStep
                  ? [
                      { label: 'Step 1 — RM Approval', value: 1 },
                      { label: 'Step 2 — Circle Head Approval', value: 2 },
                      { label: 'Step 3 — Invitation Mail', value: 3 },
                    ].find((o) => o.value === Number(resendEmailStep)) || null
                  : null
              }
              onChange={(_, opt) => setResendEmailStep(opt ? String(opt.value) : '')}
              renderInput={(params) => (
                <TextField {...params} label="Step" size="small" fullWidth />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleResendEmailClose} disabled={resendEmailLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleResendEmailSubmit}
              disabled={resendEmailLoading || !resendEmailBatchId.trim() || !resendEmailStep}
              startIcon={resendEmailLoading ? <CircularProgress size={16} /> : null}
            >
              {resendEmailLoading ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Email Modals */}
        {batchEmailData && (
          <>
            <EmailModal
              open={showEmailModal}
              onClose={handleEmailModalClose}
              onSubmit={handleEmailSubmit}
              initialEmailConfig={batchEmailData.emailConfig}
              trainingType={batchEmailData.trainingType}
              shouldStopMail={shouldStopMail}
              setShouldStopMail={setShouldStopMail}
            />
            <EmailModalCollection
              open={showEmailModalCollection}
              onClose={handleEmailModalClose}
              onSubmit={handleEmailSubmit}
              initialEmailConfig={batchEmailData.emailConfig}
              trainingType={batchEmailData.trainingType}
            />
          </>
        )}
      </Layout>
    </>
  );
};

const ChangeBatchDatesDialog = ({ open, onClose, onSubmit, batchData }) => {
  const toLocalDatetimeString = (dateVal) => {
    if (!dateVal) return '';
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [dates, setDates] = React.useState(() =>
    (batchData.dates || []).map((d) => ({
      day: d.day,
      date: toLocalDatetimeString(d.date),
    }))
  );
  const [datesEnd, setDatesEnd] = React.useState(() =>
    (batchData.datesEnd || []).map((d) => ({ date: d.date || '18:30' }))
  );
  const [submitting, setSubmitting] = React.useState(false);

  const handleDateChange = (index, value) => {
    const updated = [...dates];
    updated[index] = { ...updated[index], date: value };
    setDates(updated);
  };

  const handleEndTimeChange = (index, value) => {
    const updated = [...datesEnd];
    updated[index] = { date: value };
    setDatesEnd(updated);
  };

  const handleSubmit = async () => {
    for (const d of dates) {
      if (!d.date) {
        toast.error('Please fill in all start dates');
        return;
      }
    }
    setSubmitting(true);
    try {
      await onSubmit(dates, datesEnd);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Batch Dates — {batchData.batchId}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Updating dates will also update attendance records and training assignment logs.
        </Typography>
        {dates.map((day, idx) => (
          <Box
            key={idx}
            sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {day.day}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={7}>
                <TextField
                  label="Date & Start Time"
                  type="datetime-local"
                  value={day.date}
                  onChange={(e) => handleDateChange(idx, e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  label="End Time"
                  type="time"
                  value={datesEnd[idx]?.date || '18:30'}
                  onChange={(e) => handleEndTimeChange(idx, e.target.value)}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {submitting ? 'Saving...' : 'Save Dates'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AllTrainings;
